import { reaction, action, runInAction } from 'mobx';

import data from './data';
import { doOnParents, isAncestor } from './util';

interface HoverableParams {
	root: HTMLElement;
	// Thing that can be hovered
	hoverableSelector: string;
	// Thing that is displayed when hoverableSelector is hovered, scoped to hoverableSelector
	displaySelector: string;
}

export function initHoverable( params: HoverableParams ) {
	const { root, hoverableSelector, displaySelector } = params;

	if ( ! root.id ) {
		throw new Error( 'Root needs to have an ID attribute.' );
	}

	if ( ! data.hoverable[ root.id ] ) {
		runInAction( () => {
			data.hoverable[ root.id ] = { selected: null, oldSelected: null };
		} );
	}

	root.addEventListener(
		'mouseover',
		action( ( e ) => {
			const item = ( e.target as HTMLElement ).closest(
				hoverableSelector
			);
			if ( ! item ) {
				return;
			}
			data.hoverable[ root.id ].selected = item as HTMLElement;
		} )
	);

	root.addEventListener(
		'mouseout',
		action( ( e ) => {
			const item = ( e.target as HTMLElement ).closest(
				hoverableSelector
			);
			if ( ! item ) {
				return;
			}
			data.hoverable[ root.id ].selected = null;
			data.hoverable[ root.id ].oldSelected = item as HTMLElement;
		} )
	);

	const showDisplayable = ( hoverable ) => {
		const displayElement = hoverable.querySelector(
			`:scope ${ displaySelector }`
		) as HTMLElement | null;
		if ( ! displayElement ) {
			return;
		}
		displayElement.style.visibility = 'visible';
		displayElement.style.opacity = '1';
	};

	const hideDisplayable = ( hoverable ) => {
		const displayElement = hoverable.querySelector(
			`:scope ${ displaySelector }`
		) as HTMLElement | null;
		if ( ! displayElement ) {
			return;
		}
		displayElement.style.visibility = '';
		displayElement.style.opacity = '';
	};

	reaction(
		() => ( {
			selectedElement: data.hoverable[ root.id ].selected,
			oldSelected: data.hoverable[ root.id ].oldSelected,
		} ),
		( { selectedElement, oldSelected } ) => {
			// oldSelected must not be null. If selectedElement exists, then only hide oldSelected if oldSelected
			// is not an ancestor of selectedElement.
			if (
				oldSelected &&
				( ! selectedElement ||
					( selectedElement &&
						! isAncestor( oldSelected, selectedElement ) ) )
			) {
				doOnParents( oldSelected, hoverableSelector, hideDisplayable );
			}

			if ( selectedElement ) {
				doOnParents(
					selectedElement,
					hoverableSelector,
					showDisplayable
				);
			}
		},
		{ fireImmediately: true }
	);
}
